<template>
    <div
        :class="['ppv2-fc-root', customClass, { 'is-maximized': isMaximized }]"
        :style="isMaximized ? { ...cssVars, ...maximizedStyles } : cssVars"
    >
        <!-- Header -->
        <div
            :class="headerClasses"
            :style="{ height: normalizeSizeVertical(headerHeight || 57, true), backgroundColor: 'var(--fc-header-bg)' }"
        >
            <div class="ppv2-w-[130px]"><!-- Empty space --></div>

            <div class="ppv2-flex ppv2-justify-center ppv2-items-center ppv2-gap-4 xl:ppv2-gap-[35px]">
                <ChevronLeft :size="20" class="fc-nav-btn" :style="{ color: 'var(--fc-header-text)' }" @click="goPrev" />
                <h2
                    class="mb-0 ppv2-text-[11px] poppinsSemiBold font-inter text-capitalize"
                    :style="{ color: 'var(--fc-header-text)', whiteSpace: 'nowrap' }"
                >
                    {{ currentTitle }}
                </h2>
                <ChevronRight :size="20" class="fc-nav-btn" :style="{ color: 'var(--fc-header-text)' }" @click="goNext" />
            </div>

            <div class="ppv2-w-[130px] ppv2-flex ppv2-items-center ppv2-justify-end ppv2-gap-2">
                <BaseSelect
                    v-model="currentView"
                    :options="optionOfTypeGrid"
                    :isChevronsUpDown="false"
                    :searchable="false"
                    :inCalendar="true"
                    option-label="label"
                    option-value="value"
                    @input="changeView"
                    classAdded="ppv2-rounded-full"
                    classContainerAdded="!ppv2-opacity-100"
                    fixed-dropdown
                />
                <button
                    class="ppv2-p-1 ppv2-rounded-md hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-transition-all"
                    style="color: var(--fc-header-text)"
                    @click="toggleMaximized"
                >
                    <Minimize v-if="isMaximized" :size="20" />
                    <Fullscreen v-else :size="20" />
                </button>
            </div>
        </div>

        <!-- Calendar -->
        <FullCalendar
            ref="fullCalendar"
            :options="calendarOptions"
            @date-click="handleDateClick"
            @event-click="handleEventClick"
        />

        <!-- Dialog -->
        <CustomDialog
            v-model="dialogVisible"
            @cancel="closeDialog"
            :isInPopup="true"
            :width="dialogWidth"
            :height="dialogHeight"
            :minHeight="dialogHeight"
        >
            <template #content>
                <div class="ppv2-px-4 ppv2-py-3">
                    <!-- Date Picker -->
                    <div v-if="!isDateRange" class="ppv2-mb-6">
                        <BaseDateInput
                            v-model="dateStart"
                            :min-date="minDate"
                            :placeholder="formattedDateStart || $t('resource_planning.exceptions.today')"
                            :label="$t('resource_planning.weekRangeScheduling.date_begin_label')"
                            displayFormat="dddd D MMMM YYYY"
                        />
                        <p v-if="errors.dateStart" class="fc-error-text ppv2-text-sm ppv2-mt-1">
                            {{ errors.dateStart }}
                        </p>
                    </div>

                    <!-- Date Range Picker -->
                    <div v-else class="fc-dialog-grid-2col ppv2-mb-6">
                        <div>
                            <BaseDateInput
                                v-model="dateStart"
                                :min-date="minDate"
                                :placeholder="formattedDateStart || $t('resource_planning.exceptions.today')"
                                :label="$t('resource_planning.weekRangeScheduling.date_begin_label')"
                                displayFormat="dddd D MMMM YYYY"
                            />
                            <p v-if="errors.dateStart" class="fc-error-text ppv2-text-sm ppv2-mt-1">
                                {{ errors.dateStart }}
                            </p>
                        </div>
                        <div>
                            <BaseDateInput
                                v-model="dateEnd"
                                :min-date="dateStart || minDate"
                                :placeholder="formattedDateEnd || $t('resource_planning.exceptions.today')"
                                :label="$t('resource_planning.weekRangeScheduling.date_end_label')"
                                displayFormat="dddd D MMMM YYYY"
                            />
                            <p v-if="errors.dateEnd" class="fc-error-text ppv2-text-sm ppv2-mt-1">
                                {{ errors.dateEnd }}
                            </p>
                        </div>
                    </div>

                    <!-- Time Pickers -->
                    <div class="fc-dialog-grid-2col ppv2-mb-4">
                        <!-- Start Time -->
                        <div>
                            <BaseTimeInput
                                v-model="timeStart"
                                required
                                :label="$t('resource_planning.scheduling_temporary_closures.hour_begin_label')"
                            />
                            <p v-if="errors.timeStart" class="fc-error-text ppv2-text-sm ppv2-mt-1">
                                {{ errors.timeStart }}
                            </p>
                        </div>

                        <!-- End Time -->
                        <div>
                            <BaseTimeInput
                                v-model="timeEnd"
                                :min-time="timeStart"
                                required
                                :label="$t('resource_planning.scheduling_temporary_closures.hour_end_label')"
                            />
                            <p v-if="errors.timeEnd" class="fc-error-text ppv2-text-sm ppv2-mt-1">
                                {{ errors.timeEnd }}
                            </p>
                        </div>
                    </div>

                    <!-- Recurrence -->
                    <div class="ppv2-mb-6">
                        <BaseSelect
                            v-model="reccurence"
                            :options="recurrenceOptions"
                            :label="$t('experience.step4.specificMode.calendar.recurring')"
                            :searchable="false"
                            option-label="label"
                            option-value="value"
                            class="transparent-select ppv2-w-full borderColor"
                            :error="errors.reccurence"
                            fixed-dropdown
                        />
                    </div>

                    <DashedCard
                        v-if="reccurence === 'custom'"
                        :title="$t('experience.step4.specificMode.calendar.typeRecurrence.card.title')"
                        :title-size="15"
                        custom-class=""
                        custom-class-title=""
                        :marginTop="37"
                        :marginBottom="37"
                        min-height="115px"
                        :custom-style="{ padding: normalizeSize(24, true) }"
                    >
                        <template #content>
                            <div
                                class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-start ppv2-justify-start"
                                :style="{ gap: normalizeSize(16, true) }"
                            >
                                <div class="ppv2-flex ppv2-items-center ppv2-mb-2" :style="{ gap: normalizeSize(16, true) }">
                                    <!-- Label -->
                                    <BaseLabel
                                        :label="$t('experience.step4.specificMode.calendar.typeRecurrence.card.label')"
                                        customClass="!ppv2-min-h-[40px] ppv2-text-base"
                                    />

                                    <!-- Number input + chevrons -->
                                    <div class="ppv2-relative d-flex align-item-start ppv2-w-[70px] ppv2-gap-3">
                                        <CustomInput type="number" v-model="reccurenceNumber" placeholder="0" />

                                        <div class="d-flex flex-column ppv2-justify-between ppv2-h-[38px]">
                                            <!-- Up -->
                                            <CircleChevronUp :size="15" class="ppv2-cursor-pointer" @click="increment" />

                                            <!-- Down -->
                                            <CircleChevronDown :size="15" class="ppv2-cursor-pointer" @click="decrement" />
                                        </div>
                                    </div>

                                    <!-- Select -->
                                    <BaseSelect
                                        v-model="typeReccurence"
                                        :options="recurrenceTypeOptions"
                                        :searchable="false"
                                        option-label="label"
                                        option-value="value"
                                        class="transparent-select ppv2-w-[130px] borderColor ppv2-mb-4 ppv2-mr-auto"
                                        :error="errors.reccurence"
                                        fixed-dropdown
                                    />
                                </div>
                            </div>
                            <div class="ppv2-flex ppv2-flex-col" :style="{ marginBottom: normalizeSizeVertical(35, true) }">
                                <BaseLabel
                                    :label="$t('experience.step4.specificMode.calendar.typeRecurrence.card.labelDay')"
                                    customClass="ppv2-text-base"
                                />
                                <div v-show="typeReccurence !== 'month'" class="ppv2-flex ppv2-flex-wrap ppv2-gap-2">
                                    <BaseCircleButton
                                        v-for="day in days"
                                        :key="day.id"
                                        :active="selectedDays.includes(day.id)"
                                        @click="toggleDay(day.id)"
                                    >
                                        {{ day.label }}
                                    </BaseCircleButton>
                                    <p v-if="errors.selectedDays" class="fc-error-text ppv2-text-xs ppv2-mt-1">
                                        {{ errors.selectedDays }}
                                    </p>
                                </div>
                            </div>

                            <BaseSelect
                                v-if="typeReccurence === 'month'"
                                v-model="monthTypeReccurence"
                                :options="monthRecurrenceOptions"
                                :searchable="false"
                                option-label="label"
                                option-value="value"
                                class="transparent-select"
                                classAdded="ppv2-w-full"
                                :error="errors.monthTypeReccurence"
                                fixed-dropdown
                            />

                            <div class="ppv2-flex ppv2-items-center ppv2-mb-2" :style="{ gap: normalizeSize(16, true) }">
                                <!-- Left: SelectableList -->
                                <div class="ppv2-w-auto">
                                    <SelectableList
                                        :label="$t('experience.step4.specificMode.calendar.typeRecurrence.card.labelEnd')"
                                        v-model="endCheck"
                                        :options="optionEndCheck"
                                        :withoutBorder="true"
                                        :inSameLine="false"
                                    />
                                </div>

                                <!-- Right: BaseDateInput -->
                                <div v-show="endCheck === false" class="ppv2-flex-1">
                                    <BaseDateInput
                                        v-model="dateEnd"
                                        :min-date="minDate"
                                        :placeholder="formattedDateStart || $t('resource_planning.exceptions.today')"
                                        displayFormat="dddd D MMMM YYYY"
                                        :disabled="endCheck === true"
                                        class="ppv2-mt-6"
                                        inputClass="ppv2-absolute"
                                    />
                                    <p v-if="errors.dateEnd" class="fc-error-text ppv2-text-sm ppv2-mt-1">
                                        {{ errors.dateEnd }}
                                    </p>
                                </div>
                            </div>
                        </template>
                    </DashedCard>

                    <!-- Checkbox -->
                    <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                        <BaseCheckbox
                            v-model="isReccurent"
                            :label="$t('experience.step4.specificMode.calendar.confirmCheckbox')"
                        />
                    </div>
                </div>
            </template>

            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="primary"
                    customClass="!ppv2-min-h-[40px]"
                    @click="confirmDialog"
                    :customHeight="43"
                >
                    {{ $t('common.save') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Event Tooltip -->
        <div v-if="tooltip.visible" class="fc-event-tooltip" :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }">
            {{ tooltip.text }}
        </div>
    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import CustomDialog from './CustomDialog.vue'
import { ChevronRight, ChevronLeft, ChevronsUpDown, Fullscreen, Minimize } from 'lucide-vue'
import BaseSelect from './BaseSelect.vue'
import normalizeSizeVertical from '../../utils/normalizeSizeVertical'
import BaseDateInput from './BaseDateInput.vue'
import CustomInput from '../Inputs/CustomInput.vue'
import BaseTimeInput from './BaseTimeInput.vue'
import BaseCheckbox from './BaseCheckbox.vue'
import BaseButton from './BaseButton.vue'
import DashedCard from './DashedCard.vue'
import normalizeSize from '../../utils/normalizeSize'
import BaseLabel from './BaseLabel.vue'
import { CircleChevronUp } from 'lucide-vue'
import { CircleChevronDown } from 'lucide-vue'
import BaseCircleButton from './BaseCircleButton.vue'
import SelectableList from './SelectableList.vue'

export default {
    name: 'CalendarFull',
    components: {
        BaseCheckbox,
        BaseButton,
        BaseTimeInput,
        BaseDateInput,
        ChevronsUpDown,
        BaseSelect,
        FullCalendar,
        CustomDialog,
        ChevronRight,
        ChevronLeft,
        DashedCard,
        CustomInput,
        BaseLabel,
        CircleChevronUp,
        CircleChevronDown,
        BaseCircleButton,
        SelectableList,
        Fullscreen,
        Minimize,
    },

    props: {
        subCategoryId: { type: [Number, String], default: null },
        establishmentId: { type: [Number, String], default: null },
        prestation: { type: Object, default: null },
        externalEvents: { type: Array, default: () => [] },
        externalCollisionCheck: { type: Function, default: null },
        initialCheckedDates: { type: Array, default: () => [] },
        editingIndex: { type: Number, default: -1 },
        fetchUrl: { type: String, default: null },
        // Styling & UX customization
        variant: { type: String, default: 'default' },
        headerBgColor: { type: String, default: null },
        headerTextColor: { type: String, default: null },
        selectedDayBgColor: { type: String, default: null },
        errorColor: { type: String, default: null },
        checkboxAccentColor: { type: String, default: null },
        dialogWidth: { type: String, default: '700px' },
        dialogHeight: { type: String, default: '550px' },
        calendarFontSize: { type: String, default: null },
        headerHeight: { type: Number, default: null },
        customClass: { type: String, default: '' },
        disableInternalDialog: { type: Boolean, default: false },
    },

    data() {
        return {
            isMaximized: false,
            maximizedStyles: {},
            currentView: 'dayGridMonth',
            currentTitle: '',
            minDate: new Date(),
            dialogVisible: false,
            dateStart: null,
            timeStart: null,
            timeEnd: null,
            formattedDateStart: '',
            formattedDateEnd: '',
            isDateRange: false,
            errors: {},
            reccurence: '',
            typeReccurence: 'week',
            reccurenceNumber: 1,
            dateEnd: null,
            isReccurent: false,
            selectedDate: null,
            selectedEvent: null,
            monthTypeReccurence: 'day_of_month',
            days: [
                { id: 'Monday', label: this.$t('experience.timeSpecific.days.mon') },
                { id: 'Tuesday', label: this.$t('experience.timeSpecific.days.tue') },
                { id: 'Wednesday', label: this.$t('experience.timeSpecific.days.wed') },
                { id: 'Thursday', label: this.$t('experience.timeSpecific.days.thu') },
                { id: 'Friday', label: this.$t('experience.timeSpecific.days.fri') },
                { id: 'Saturday', label: this.$t('experience.timeSpecific.days.sat') },
                { id: 'Sunday', label: this.$t('experience.timeSpecific.days.sun') },
            ],
            selectedDays: [],
            endCheck: true,
            optionEndCheck: [
                {
                    label: this.$t('experience.step4.specificMode.calendar.typeRecurrence.card.checkNever'),
                    value: true,
                    subtitle: '',
                },
                {
                    label: this.$t('experience.step4.specificMode.calendar.typeRecurrence.card.checkLe'),
                    value: false,
                    subtitle: '',
                },
            ],
            optionOfTypeGrid: [
                { label: this.$t('resource_planning.exceptions.month'), value: 'dayGridMonth' },
                { label: this.$t('resource_planning.week_number'), value: 'timeGridWeek' },
                { label: this.$t('resource_planning.exceptions.day'), value: 'timeGridDay' },
            ],
            currentApiEvents: [],
            checkedDates: [],
            tooltip: { visible: false, text: '', x: 0, y: 0 },
            calendarOptions: {
                plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
                initialView: 'dayGridMonth',
                locale: 'fr',
                editable: true,
                firstDay: 1, //commence par lundi
                selectable: true,
                aspectRatio: 1.4,
                dateClick: this.handleDateClick,
                eventClick: this.handleEventClick,
                headerToolbar: false,
                eventDisplay: 'block',
                dayHeaderFormat: { weekday: 'long' },
                events: this.fetchEvents,
                dayCellClassNames: (arg) => {
                    const today = new Date()
                    today.setHours(0, 0, 0, 0)
                    if (arg.date < today) {
                        return ['fc-day-past']
                    }
                    return []
                },
                dayCellDidMount: (arg) => {
                    const today = new Date()
                    today.setHours(0, 0, 0, 0)
                    if (arg.date < today) return

                    const dateStr = this.formatDate(arg.date)
                    const wrapper = document.createElement('div')
                    wrapper.className = 'fc-day-checkbox-wrapper'

                    const checkbox = document.createElement('input')
                    checkbox.type = 'checkbox'
                    checkbox.className = 'fc-day-checkbox'
                    checkbox.checked = this.checkedDates.includes(dateStr)
                    checkbox.dataset.date = dateStr

                    checkbox.addEventListener('click', (e) => {
                        e.stopPropagation()
                    })
                    checkbox.addEventListener('change', (e) => {
                        e.stopPropagation()
                        const idx = this.checkedDates.indexOf(dateStr)
                        if (checkbox.checked) {
                            if (idx === -1) this.checkedDates.push(dateStr)
                            arg.el.classList.add('fc-day-selected')
                        } else {
                            if (idx !== -1) this.checkedDates.splice(idx, 1)
                            arg.el.classList.remove('fc-day-selected')
                        }
                    })

                    wrapper.appendChild(checkbox)
                    arg.el.style.position = 'relative'
                    if (this.checkedDates.includes(dateStr)) {
                        arg.el.classList.add('fc-day-selected')
                    }
                    arg.el.appendChild(wrapper)
                },
                dayCellWillUnmount: (arg) => {
                    const wrapper = arg.el.querySelector('.fc-day-checkbox-wrapper')
                    if (wrapper) wrapper.remove()
                    arg.el.classList.remove('fc-day-selected')
                },
                eventContent(arg) {
                    const fullTitle = arg.event.title || ''
                    const props = arg.event.extendedProps || {}
                    const name = props.eventName || fullTitle
                    const timeRange = props.timeRange || ''

                    // Max 3 words for the name
                    const words = name.split(/\s+/)
                    const shortName = words.length > 3 ? words.slice(0, 3).join(' ') + '...' : name
                    const safeName = shortName.replace(/</g, '&lt;').replace(/>/g, '&gt;')
                    const safeTime = timeRange.replace(/</g, '&lt;').replace(/>/g, '&gt;')
                    const safeFullTitle = fullTitle.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

                    return {
                        html: `<div class="fc-event-text" title="${safeFullTitle}"><div class="fc-event-name">${safeName}</div>${
                            safeTime ? `<div class="fc-event-time">${safeTime}</div>` : ''
                        }</div>`,
                    }
                },
                datesSet: (info) => {
                    this.currentTitle = info.view.title
                },
                eventMouseEnter: (arg) => {
                    const rect = arg.el.getBoundingClientRect()
                    const parentRect = this.$el.getBoundingClientRect()
                    this.tooltip.visible = true
                    this.tooltip.text = arg.event.title
                    this.tooltip.x = rect.left - parentRect.left
                    this.tooltip.y = rect.bottom - parentRect.top + 6
                },
                eventMouseLeave: () => {
                    this.tooltip.visible = false
                },
            },
        }
    },

    mounted() {
        window.addEventListener('resize', this.updateMaximizedStyles)
        this.$nextTick(() => {
            const api = this.$refs.fullCalendar?.getApi?.()
            if (api) api.updateSize()
        })
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.updateMaximizedStyles)
        document.documentElement.style.overflow = ''
    },

    computed: {
        isDark() {
            return this.theme.mode === 'dark'
        },
        cssVars() {
            const dark = this.isDark || this.variant === 'dark'
            return {
                '--fc-header-bg': this.headerBgColor || (dark ? '#FFFFFF1A' : '#7D2DBD26'),
                '--fc-header-text': this.headerTextColor || (dark ? '#FFFFFF' : '#11102A'),
                '--fc-dayheader-bg': dark ? '#11102A' : '#7D2DBD',
                '--fc-dayheader-text': '#FFFFFF',
                '--fc-selected-bg': this.selectedDayBgColor || (dark ? '#1E1B4B' : '#F3E8FF'),
                '--fc-error-color': this.errorColor || '#ed4b9e',
                '--fc-checkbox-accent': this.checkboxAccentColor || (dark ? '#7d2dbd' : '#7d2dbd'),
                '--fc-checkbox-border': this.checkboxAccentColor || (dark ? '#7d2dbd' : '#7d2dbd'),
                '--fc-font-size': this.calendarFontSize || '0.9rem',
                '--fc-past-bg': dark ? '#0A0A1C' : '#f1f5f9',
                '--fc-day-hover-bg': dark ? '#F8F8F81A' : '#E8E8E81A',
                // FullCalendar native CSS variables — applied automatically
                '--fc-page-bg-color': dark ? '#11102A' : '#fff',
                '--fc-today-bg-color': dark ? 'rgba(125,45,189,0.15)' : 'rgba(125,45,189,0.05)',
                '--fc-neutral-bg-color': dark ? '#1E1B4B' : 'rgba(208,208,208,0.3)',
                '--fc-border-color': dark ? '#1E1B4B' : '#E9E4F1',
                '--fc-event-bg-color': dark ? '#7D2DBD' : '#7D2DBD',
                '--fc-event-border-color': dark ? '#7D2DBD' : '#7D2DBD',
                '--fc-event-text-color': '#fff',
                '--fc-list-event-hover-bg-color': dark ? '#1E1B4B' : 'rgba(0,0,0,0.05)',
                '--fc-tooltip-bg': dark ? '#0f172a' : '#11102A',
                '--fc-tooltip-text': '#f8fafc',
            }
        },
        headerClasses() {
            return ['ppv2-flex', 'ppv2-justify-between', 'ppv2-items-center', 'ppv2-rounded-t-xl', 'ppv2-px-3', 'ppv2-py-1']
        },
        recurrenceOptions() {
            const dayName = this.getDayName(this.dateStart)
            const dayCapitalized = dayName.charAt(0).toUpperCase() + dayName.slice(1)
            const lastDay = this.getLastDayOfMonth(this.dateStart)
            const monthName = this.getMonthName(this.dateStart)

            return [
                { value: 'once', label: this.$t('experience.step4.specificMode.calendar.typeRecurrence.once') },
                {
                    value: 'weekly',
                    label: this.$t('experience.step4.specificMode.calendar.typeRecurrence.weekly', { day: dayName }),
                },
                {
                    value: 'monthly',
                    label: this.$t('experience.step4.specificMode.calendar.typeRecurrence.monthly', { day: dayCapitalized }),
                },
                /*{
                    value: 'yearly',
                    label: this.$t('experience.step4.specificMode.calendar.typeRecurrence.yearly', {
                        day: lastDay,
                        month: monthName,
                    }),
                },*/
                { value: 'weekdays', label: this.$t('experience.step4.specificMode.calendar.typeRecurrence.weekdays') },
                { value: 'custom', label: this.$t('experience.step4.specificMode.calendar.typeRecurrence.custom') },
            ]
        },
        recurrenceTypeOptions() {
            return [
                { value: 'week', label: 'semaine' },
                { value: 'month', label: 'mois' },
            ]
        },
        monthRecurrenceOptions() {
            const dateStart = new Date(this.dateStart)
            const day = dateStart.getDate()
            const dayName = dateStart.toLocaleDateString('fr-FR', { weekday: 'long' })
            return [
                {
                    label: `Tous les ${day} du mois`,
                    value: 'day_of_month',
                },
                {
                    label: `Tous les derniers ${dayName} du mois`,
                    value: 'last_weekday',
                },
            ]
        },
    },

    methods: {
        normalizeSizeVertical,
        normalizeSize,

        toggleMaximized() {
            this.isMaximized = !this.isMaximized
            if (this.isMaximized) {
                window.scrollTo(0, 0)
                this.updateMaximizedStyles()
                this.calendarOptions.height = '100%'
                this.calendarOptions.contentHeight = '100%'
                document.documentElement.style.overflow = 'hidden'
            } else {
                this.maximizedStyles = {}
                this.calendarOptions.height = 'auto'
                this.calendarOptions.contentHeight = 'auto'
                document.documentElement.style.overflow = ''
            }
            this.$nextTick(() => {
                this.handleSizeChange()
            })
        },

        updateMaximizedStyles() {
            if (!this.isMaximized) return

            const main = document.getElementById('main-container')
            const header =
                document.getElementById('v3_design_navbar') ||
                document.getElementById('page-header') ||
                document.querySelector('.bo-header-v3') ||
                document.querySelector('.experience-navbar')

            let left = 280
            let top = 70
            let width = window.innerWidth - 280

            if (main) {
                const mainRect = main.getBoundingClientRect()
                left = mainRect.left
                width = mainRect.width
                top = mainRect.top
            }

            if (header) {
                const headerRect = header.getBoundingClientRect()
                if (headerRect.bottom > top) {
                    top = headerRect.bottom
                }
            }

            // Fallback for extremely small top
            if (top < 0) top = 0

            this.maximizedStyles = {
                position: 'fixed',
                top: `95px`,
                left: `${left}px`,
                width: `${width}px`,
                height: `${window.innerHeight - 95}px`,
                zIndex: '9999',
                background: 'var(--fc-page-bg-color, #fff)',
                padding: '20px',
                borderRadius: '22px',
            }
        },

        handleSizeChange() {
            // Force FullCalendar to update its size and render multiple times to ensure it catches the transition
            const update = () => {
                if (this.$refs.fullCalendar) {
                    const api = this.$refs.fullCalendar.getApi()
                    api.updateSize()
                    api.render()
                }
                window.dispatchEvent(new Event('resize'))
            }

            // Immediate update
            update()

            // Update after transition
            setTimeout(update, 350)

            // Final fallback update
            setTimeout(update, 1000)
        },

        async fetchEvents(info, successCallback, failureCallback) {
            let apiEvents = []
            let prestation_id = this.prestation ? this.prestation.id : null

            // Use fetchUrl prop if provided (V3 API), otherwise fall back to old back-office URL
            const url = this.fetchUrl
                ? this.fetchUrl
                : this.subCategoryId && this.establishmentId
                  ? `/back-office/prestation/${this.subCategoryId}/planning/${this.establishmentId}/`
                  : null

            if (url) {
                try {
                    const response = await axios.get(url, {
                        params: { start: info.startStr, end: info.endStr },
                    })
                    if (this.fetchUrl) {
                        const raw = response.data
                        apiEvents = Array.isArray(raw)
                            ? raw
                            : Object.values(raw).filter((v) => typeof v === 'object' && v !== null)
                    } else {
                        apiEvents = response.data.filter((ev) => ev.prestation_id === prestation_id)
                    }
                    this.currentApiEvents = apiEvents
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                }
            }

            // Map externalEvents to FullCalendar format
            const localEvents = this.externalEvents.map((ev) => {
                const startShort = (ev.timeStart || '').substring(0, 5)
                const endShort = (ev.timeEnd || '').substring(0, 5)
                // Opt-in : un événement externe peut imposer son libellé (ex. le prix d'un tarif
                // horaire de billetterie) et sa plage affichée ('' = rien). Sans eux, rendu inchangé.
                const eventName = ev.eventName || this.prestation?.name || 'Event'
                const timeRange = ev.timeRange !== undefined ? ev.timeRange : `${startShort} – ${endShort}`
                const fcEvent = {
                    id: ev.tmp_id || ev.id || Date.now() + Math.random(),
                    title: ev.title || `${eventName} • ${startShort}–${endShort}`,
                    allDay: true,
                    extendedProps: {
                        eventName: eventName,
                        timeRange: timeRange,
                    },
                }

                if (ev.selectedDays && ev.selectedDays.length > 0 && ev.dateStart !== ev.dateEnd) {
                    // Recurring event (FullCalendar endRecur is exclusive → +1 day)
                    const dayMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 }
                    const days = ev.selectedDays
                        .flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d]))
                        .filter(Boolean)
                    fcEvent.daysOfWeek = days.map((d) => dayMap[d]).filter((v) => v !== undefined)
                    fcEvent.startTime = ev.timeStart
                    fcEvent.endTime = ev.timeEnd
                    fcEvent.startRecur = ev.dateStart
                    const endDate = new Date(ev.dateEnd)
                    endDate.setDate(endDate.getDate() + 1)
                    fcEvent.endRecur = endDate.toISOString().slice(0, 10)
                } else {
                    // Single day event (allDay → end exclusive, +1 day)
                    fcEvent.start = ev.dateStart
                    const endBase = ev.dateEnd || ev.dateStart
                    const endDate = new Date(endBase)
                    endDate.setDate(endDate.getDate() + 1)
                    fcEvent.end = endDate.toISOString().slice(0, 10)
                }
                return fcEvent
            })

            successCallback([...apiEvents, ...localEvents])
        },

        changeView() {
            const api = this.$refs.fullCalendar.getApi()
            api.changeView(this.currentView)
            this.currentTitle = api.view.title
        },

        goPrev() {
            const api = this.$refs.fullCalendar.getApi()
            api.prev()
            this.currentTitle = api.view.title
        },

        goNext() {
            const api = this.$refs.fullCalendar.getApi()
            api.next()
            this.currentTitle = api.view.title
        },

        formatDate(date) {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        },

        formatTime(date) {
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            return `${hours}:${minutes}`
        },

        cancelCheckedDates() {
            this.checkedDates = []
            const api = this.$refs.fullCalendar && this.$refs.fullCalendar.getApi ? this.$refs.fullCalendar.getApi() : null
            if (api) {
                api.render()
            }
        },
        setCheckedDates(dates) {
            this.checkedDates = Array.isArray(dates) ? [...new Set(dates)] : []
            this.$nextTick(() => {
                const api = this.$refs.fullCalendar && this.$refs.fullCalendar.getApi ? this.$refs.fullCalendar.getApi() : null
                if (api) api.render()
            })
        },
        refresh() {
            this.$nextTick(() => {
                const api = this.$refs.fullCalendar?.getApi?.()
                if (api) api.refetchEvents()
            })
        },

        openCheckedDatesDialog() {
            if (!this.checkedDates.length) return

            const sortedDates = [...this.checkedDates].sort((a, b) => new Date(a) - new Date(b))
            this.errors = {}
            this.dialogVisible = true
            this.timeStart = this.timeStart || '09:00'
            this.timeEnd = this.timeEnd || '17:00'

            if (sortedDates.length === 1) {
                this.isDateRange = false
                this.dateStart = sortedDates[0]
                this.dateEnd = null
                this.selectedDate = sortedDates[0]
                return
            }

            this.isDateRange = true
            this.dateStart = sortedDates[0]
            this.dateEnd = sortedDates[sortedDates.length - 1]
            this.selectedDate = null
        },

        handleDateClick(info) {
            // Skip if clicking on a checkbox
            if (info.jsEvent && info.jsEvent.target && info.jsEvent.target.closest('.fc-day-checkbox-wrapper')) {
                return
            }

            this.$emit('date-click', info)

            // If fetchUrl is set or parent explicitly disables internal dialog
            if (this.fetchUrl || this.disableInternalDialog) return

            const clickedDate = new Date(info.dateStr)
            const today = new Date()
            today.setHours(0, 0, 0, 0)

            if (clickedDate < today) {
                return
            }

            this.dialogVisible = true
            this.isDateRange = false
            this.selectedDate = info.dateStr
            this.dateStart = this.formatDate(info.date)
            this.dateEnd = null
            this.timeStart = '09:00'
            this.timeEnd = '17:00'
        },

        handleEventClick(info) {
            this.$emit('event-click', info)

            // If fetchUrl is set or parent explicitly disables internal dialog
            if (this.fetchUrl || this.disableInternalDialog) return

            this.dialogVisible = true
            this.selectedEvent = info.event

            if (info.event.start) this.dateStart = this.formatDate(info.event.start)
            if (info.event.start) this.timeStart = this.formatTime(info.event.start)
            this.timeEnd = info.event.end ? this.formatTime(info.event.end) : ''
        },

        closeDialog() {
            this.dialogVisible = false
        },
        confirmDialog() {
            this.errors = {}

            if (!this.validateRecurrence()) return

            if (this.reccurence === 'custom' && this.typeReccurence === 'week') {
                this.handleCustomWeeklyRecurrence()
                return
            }

            if (this.typeReccurence === 'month') {
                this.handleMonthlyRecurrence()
                return
            }

            // Check collision for standard recurrence types if not handled by sub-methods
            if (this.reccurence === 'once' || this.reccurence === 'weekly' || this.reccurence === 'weekdays') {
                const dayNames = this.getDayNames()
                const emitData = this.baseEmitData()
                if (this.reccurence === 'weekly') this.handleWeekly(emitData, dayNames)
                else if (this.reccurence === 'weekdays') this.handleWeekdays(emitData)
                else if (this.reccurence === 'once') {
                    if (!this.handleOnce(emitData, dayNames)) return
                }

                if (this.externalCollisionCheck) {
                    if (this.externalCollisionCheck(emitData)) {
                        this.errors = {
                            ...this.errors,
                            dateStart: this.$t('experience.errors.overlapDisponibility') || 'Collision détectée.',
                        }
                        return
                    }
                }
                this.$emit('save-rule', emitData)
                this.closeDialog()
                return
            }

            this.handleStandardRecurrence()
        },

        validateRecurrence() {
            if (!this.reccurence) {
                this.errors = {
                    ...this.errors,
                    reccurence: this.$t('common.placeholderSelect'),
                }
                return false
            }
            return true
        },
        validateSelectedDays() {
            if (!this.selectedDays || this.selectedDays.length === 0) {
                this.errors = {
                    ...this.errors,
                    selectedDays: this.$t('common.placeholderSelect'),
                }
                return false
            }
            return true
        },

        resolveLimitDate() {
            if (this.endCheck) {
                const d = new Date(this.dateStart)
                d.setFullYear(d.getFullYear() + 1)
                return d
            }

            if (!this.dateEnd) {
                this.errors = {
                    ...this.errors,
                    dateEnd: this.$t('common.placeholderSelect'),
                }
                return null
            }

            return new Date(this.dateEnd)
        },

        handleCustomWeeklyRecurrence() {
            if (!this.validateSelectedDays()) return

            const limitDate = this.resolveLimitDate()
            if (!limitDate) return
            // Adjust limitDate to be inclusive for FullCalendar (exclusive end)
            limitDate.setDate(limitDate.getDate() + 1)

            const dayNames = this.getDayNames()
            // Anchor to the start of the week (Monday) for the recurrence cycles
            let currentWeekStart = this.getStartOfWeek(new Date(this.dateStart))

            // Parse recurrence number (weeks to add)
            const weeksToAdd = parseInt(this.reccurenceNumber) || 1

            while (currentWeekStart <= limitDate) {
                // Calculate the end of this week block (Next Monday, exclusive for FC usually, but we define the window)
                // We define the block as 7 days from the Monday.
                let loopEnd = new Date(currentWeekStart)
                loopEnd.setDate(loopEnd.getDate() + 7)

                // For the Start of the block, we must respect the user's dateStart for the FIRST week.
                // Or any time the calculated weekStart is before dateStart.
                let effectiveStart = new Date(currentWeekStart)
                if (effectiveStart < new Date(this.dateStart)) {
                    effectiveStart = new Date(this.dateStart)
                }

                // If effectiveStart is beyond our limit, we are done
                if (effectiveStart > limitDate) break

                // Clip the end if it exceeds the limitDate
                let effectiveEnd = new Date(loopEnd)
                if (effectiveEnd > limitDate) {
                    effectiveEnd = new Date(limitDate)
                    // Ensure we include the limit date if it's the specific end selection?
                    // Currently adhering to strict limit logic.
                }

                // If after clipping, start >= end, skip (or valid? if equal? emitRule logic handles start=end?)
                // check collision and emit
                if (effectiveStart <= effectiveEnd) {
                    if (!this.validateBlockDays(effectiveStart, effectiveEnd, dayNames)) return

                    const emitData = {
                        dateStart: this.formatDate(effectiveStart),
                        dateEnd: this.formatDate(effectiveEnd),
                        timeStart: this.timeStart,
                        timeEnd: this.timeEnd,
                        selectedDays: [...this.selectedDays],
                    }

                    if (this.externalCollisionCheck) {
                        if (this.externalCollisionCheck(emitData)) {
                            this.errors = {
                                ...this.errors,
                                dateStart: this.$t('experience.errors.overlapDisponibility') || 'Collision détectée.',
                            }
                            return
                        }
                    }

                    this.$emit('save-rule', emitData)
                }

                // Advance by X weeks
                currentWeekStart.setDate(currentWeekStart.getDate() + weeksToAdd * 7)
            }

            this.closeDialog()
        },

        computeBlockEnd(start, limit) {
            const end = new Date(start)
            end.setDate(end.getDate() + 6)
            return end > limit ? new Date(limit) : end
        },

        validateBlockDays(start, end, dayNames) {
            let temp = new Date(start)

            while (temp <= end) {
                const dayName = dayNames[temp.getDay()]
                if (this.selectedDays.includes(dayName)) {
                    const dateStr = this.formatDate(temp)
                    if (this.checkCollision(dateStr)) {
                        this.errors = {
                            ...this.errors,
                            dateStart: `La date ${dateStr} est déjà occupée.`,
                        }
                        return false
                    }
                }
                temp.setDate(temp.getDate() + 1)
            }

            return true
        },

        emitRule(start, end) {
            this.$emit('save-rule', {
                dateStart: this.formatDate(start),
                dateEnd: this.formatDate(end),
                timeStart: this.timeStart,
                timeEnd: this.timeEnd,
                selectedDays: [...this.selectedDays],
            })
        },
        handleStandardRecurrence() {
            const dayNames = this.getDayNames()
            let emitData = this.baseEmitData()

            if (this.reccurence === 'weekly') {
                this.handleWeekly(emitData, dayNames)
            } else if (this.reccurence === 'weekdays') {
                this.handleWeekdays(emitData)
            } else if (this.reccurence === 'monthly') {
                this.handleMonthly(dayNames)
                return
            } else if (this.reccurence === 'once') {
                if (!this.handleOnce(emitData, dayNames)) return
            }

            this.$emit('save-rule', emitData)
            this.closeDialog()
        },

        handleWeekly(data, dayNames) {
            const date = new Date(this.dateStart)
            data.selectedDays = [dayNames[date.getDay()]]
            data.dateEnd = this.oneYearLater(this.dateStart)
        },

        handleWeekdays(data) {
            data.selectedDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
            data.dateEnd = this.oneYearLater(this.dateStart)
        },

        handleMonthly(dayNames) {
            const start = new Date(this.dateStart)
            const dayOfWeek = start.getDay()

            for (let i = 0; i < 12; i++) {
                let targetDate

                if (i === 0) {
                    targetDate = start
                } else {
                    targetDate = this.firstWeekdayOfMonth(start, i, dayOfWeek)
                }

                const dateStr = this.formatDate(targetDate)
                if (this.checkCollision(dateStr)) {
                    this.errors = {
                        ...this.errors,
                        dateStart: `La date ${dateStr} est déjà occupée.`,
                    }
                    return
                }

                this.$emit('save-rule', {
                    dateStart: dateStr,
                    dateEnd: dateStr,
                    timeStart: this.timeStart,
                    timeEnd: this.timeEnd,
                    selectedDays: [dayNames[targetDate.getDay()]],
                })
            }

            this.closeDialog()
        },

        handleOnce(data, dayNames) {
            if (this.isDateRange && this.dateEnd) {
                // Date-range mode (from checkbox multi-selection): cover all days between start and end
                const start = new Date(this.dateStart)
                const end = new Date(this.dateEnd)
                if (start > end) {
                    this.errors = { dateStart: 'La date de début doit être avant la date de fin.' }
                    return false
                }
                const days = new Set()
                const temp = new Date(start)
                while (temp <= end) {
                    days.add(dayNames[temp.getDay()])
                    const dateStr = this.formatDate(temp)
                    if (this.checkCollision(dateStr)) {
                        this.errors = {
                            ...this.errors,
                            dateStart: `La date ${dateStr} est déjà occupée.`,
                        }
                        return false
                    }
                    temp.setDate(temp.getDate() + 1)
                }
                data.selectedDays = [...days]
            } else {
                if (this.checkCollision(this.dateStart)) {
                    this.errors = {
                        ...this.errors,
                        dateStart: 'Cette date est déjà occupée.',
                    }
                    return false
                }
                const date = new Date(this.dateStart)
                data.selectedDays = [dayNames[date.getDay()]]
            }
            return true
        },

        getDayNames() {
            return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        },
        baseEmitData() {
            return {
                dateStart: this.dateStart,
                dateEnd: this.dateEnd || this.dateStart,
                timeStart: this.timeStart,
                timeEnd: this.timeEnd,
                selectedDays: [],
                lunchStart: null,
                lunchEnd: null,
                isPeack: false,
            }
        },

        oneYearLater(dateStr) {
            const d = new Date(dateStr)
            d.setFullYear(d.getFullYear() + 1)
            return this.formatDate(d)
        },

        firstWeekdayOfMonth(startDate, monthOffset, dayOfWeek) {
            const d = new Date(startDate.getFullYear(), startDate.getMonth() + monthOffset, 1)

            while (d.getDay() !== dayOfWeek) {
                d.setDate(d.getDate() + 1)
            }

            return d
        },

        closeDialog() {
            this.dialogVisible = false
            this.selectedEvent = null
        },

        checkCollision(dateStr) {
            // Check externalEvents
            const isExternalCollision = this.externalEvents.some((ev, idx) => {
                // Skip the entry currently being edited
                if (this.editingIndex >= 0 && idx === this.editingIndex) return false

                let dateOverlap = false
                if (ev.selectedDays && ev.selectedDays.length > 0) {
                    // Flatten comma-joined selectedDays
                    const days = Array.isArray(ev.selectedDays)
                        ? ev.selectedDays
                              .flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d]))
                              .filter(Boolean)
                        : typeof ev.selectedDays === 'string'
                          ? ev.selectedDays
                                .split(',')
                                .map((s) => s.trim())
                                .filter(Boolean)
                          : []
                    // Recurring
                    // Check if dateStr is within range
                    if (dateStr >= ev.dateStart && dateStr <= ev.dateEnd) {
                        // Check day of week
                        const dayName = new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long' })
                        dateOverlap = days.includes(dayName)
                    }
                } else {
                    // Single
                    dateOverlap = ev.dateStart === dateStr
                }

                if (dateOverlap) {
                    // Check Time Range Overlap
                    // (StartA < EndB) and (StartB < EndA)
                    if (this.timeStart < ev.timeEnd && ev.timeStart < this.timeEnd) {
                        return true // Both date and time overlap
                    }
                }
                return false
            })

            if (isExternalCollision) return true

            // Check apiEvents
            const isApiCollision = this.currentApiEvents.some((ev) => {
                // Assume ev.start is the date string or ISO
                const evStart = ev.start.substring(0, 10)
                return evStart === dateStr
            })

            return isApiCollision
        },

        getDayName(dateStr) {
            if (!dateStr) return ''
            return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long' })
        },

        getMonthName(dateStr) {
            if (!dateStr) return ''
            return new Date(dateStr).toLocaleDateString('fr-FR', { month: 'long' })
        },

        getLastDayOfMonth(dateStr) {
            if (!dateStr) return ''
            const date = new Date(dateStr)
            return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
        },
        increment() {
            this.reccurenceNumber++
        },
        decrement() {
            if (this.reccurenceNumber > 0) {
                this.reccurenceNumber--
            }
        },
        toggleDay(dayId) {
            const index = this.selectedDays.indexOf(dayId)
            if (index === -1) this.selectedDays.push(dayId)
            else this.selectedDays.splice(index, 1)
        },

        getStartOfWeek(date) {
            const d = new Date(date)
            const day = d.getDay()
            // Adjust to Monday (1). If Sunday (0), subtract 6. Else subtract day - 1.
            const diff = d.getDate() - day + (day === 0 ? -6 : 1)
            return new Date(d.setDate(diff))
        },
        handleMonthlyRecurrence() {
            if (!this.dateStart) return

            const recurrenceNum = parseInt(this.reccurenceNumber)
            if (isNaN(recurrenceNum) || recurrenceNum < 1) return

            const [year, month, day] = this.dateStart.split('-').map(Number)
            const startDate = new Date(year, month - 1, day)
            const endDate = this.resolveLimitDate()
            if (!endDate) return

            let currentDate = new Date(startDate)
            const dayOfMonth = startDate.getDate()
            const weekday = startDate.getDay()
            const dayNames = this.getDayNames()

            // We iterate until we pass the endDate
            while (currentDate <= endDate) {
                let eventDate = null

                // CASE 1: Same day of month (e.g., every 14th)
                if (this.monthTypeReccurence === 'day_of_month') {
                    eventDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayOfMonth)
                    if (eventDate.getDate() !== dayOfMonth) {
                        if (eventDate.getMonth() !== currentDate.getMonth()) {
                            // Rolled over. Backtrack to last day of previous month (which is our target month)
                            eventDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
                        }
                    }
                }

                if (this.monthTypeReccurence === 'last_weekday') {
                    eventDate = this.getWeekdayOfMonth(currentDate.getFullYear(), currentDate.getMonth(), weekday, 'last')
                }

                if (eventDate) {
                    // Check range validation
                    if (eventDate <= endDate && eventDate >= startDate) {
                        const dateStr = this.formatDate(eventDate)

                        // Collision check
                        if (this.checkCollision(dateStr)) {
                            // If collision, maybe just show checking error and stop?
                            // Or skip? usually we stop and warn user the series has a conflict.
                            this.errors = {
                                ...this.errors,
                                dateStart: `La date ${dateStr} est déjà occupée.`,
                            }
                            return
                        }

                        const emitData = {
                            dateStart: dateStr,
                            dateEnd: dateStr,
                            timeStart: this.timeStart,
                            timeEnd: this.timeEnd,
                            selectedDays: [dayNames[eventDate.getDay()]],
                        }

                        if (this.externalCollisionCheck) {
                            if (this.externalCollisionCheck(emitData)) {
                                this.errors = {
                                    ...this.errors,
                                    dateStart: this.$t('experience.errors.overlapDisponibility') || 'Collision détectée.',
                                }
                                return
                            }
                        }

                        // Emit
                        this.$emit('save-rule', emitData)
                    }
                }

                // Advance by X months
                // We increment the MONTH of the currentDate state tracker
                // But we must be careful not to drift if we rely on `day` arithmetic.
                // Best is to increment the month on a fresh date object that represents the start anchor + N months?
                // Or just add clean months to `currentDate`.
                // `currentDate` serves as the cursor for "Month".
                // So let's just set the date to the 1st of the next target month to avoid day-overflow issues affecting the month increment.
                currentDate.setDate(1)
                currentDate.setMonth(currentDate.getMonth() + recurrenceNum)
                // We don't care about the day part of `currentDate` for the loop, only Year/Month.
                // However, for Case 1 we use `dayOfMonth` (constant) and Case 2 `weekday` (constant).
            }

            this.closeDialog()
        },
        getWeekdayOfMonth(year, month, weekday, weekOfMonth) {
            if (weekOfMonth === 'last') {
                const lastDay = new Date(year, month + 1, 0)
                const lastDayWeekday = lastDay.getDay()
                // diff: how many days to subtract to get to our target `weekday`
                // e.g. lastDay is Friday (5), we want Wednesday (3). Diff = 5 - 3 = 2. lastDay - 2.
                // e.g. lastDay is Tuesday (2), we want Wednesday (3). Diff = 2 - 3 = -1. +7 = 6. lastDay - 6.
                let diff = lastDayWeekday - weekday
                if (diff < 0) diff += 7

                const res = new Date(lastDay)
                res.setDate(lastDay.getDate() - diff)
                return res
            }
            return new Date()
        },
    },

    watch: {
        initialCheckedDates: {
            immediate: true,
            handler(val) {
                if (val && val.length) {
                    this.checkedDates = [...new Set(val)]
                    this.$nextTick(() => {
                        const api = this.$refs.fullCalendar && this.$refs.fullCalendar.getApi()
                        if (api) api.render()
                    })
                }
            },
        },
        checkedDates: {
            immediate: true,
            deep: true,
            handler(val) {
                this.$emit('checked-dates-change', [...new Set(val)])
            },
        },
        subCategoryId() {
            this.$refs.fullCalendar.getApi().refetchEvents()
        },
        establishmentId() {
            this.$refs.fullCalendar.getApi().refetchEvents()
        },
        externalEvents: {
            deep: true,
            handler() {
                this.$refs.fullCalendar.getApi().refetchEvents()
            },
        },
        dateStart(val) {
            if (val) {
                const [year, month, day] = val.split('-')
                this.formattedDateStart = `${day}/${month}/${year}`
            }
        },
        dateEnd(val) {
            if (val) {
                const [year, month, day] = val.split('-')
                this.formattedDateEnd = `${day}/${month}/${year}`
            } else {
                this.formattedDateEnd = ''
            }
        },
        endCheck(value) {
            if (value === true) {
                this.dateEnd = null
            }
        },
    },
}
</script>

<style scoped>
.fc {
    font-size: var(--fc-font-size, 0.9rem);
}
</style>

<style>
/* =====================================================================
   PAST DAYS
   ===================================================================== */
.fc-day-past {
    opacity: 0.4;
    pointer-events: none;
    cursor: not-allowed;
    background-color: var(--fc-past-bg, #f1f5f9) !important;
}

/* =====================================================================
   CHECKBOX
   ===================================================================== */
.fc-day-checkbox-wrapper {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 10;
}

.fc-day-checkbox {
    width: 13px;
    height: 13px;
    cursor: pointer;
    accent-color: var(--fc-checkbox-accent, #000);
    border: 1px solid var(--fc-checkbox-border, #000);
    border-radius: 3px;
    transition:
        transform 0.15s ease,
        box-shadow 0.15s ease;
}

.fc-day-checkbox:hover {
    transform: scale(1.2);
    box-shadow: 0 0 0 2px var(--fc-selected-bg, #dbeafe);
}

/* =====================================================================
   SELECTED DAY
   ===================================================================== */
.fc-day-selected {
    background-color: var(--fc-selected-bg, #dbeafe) !important;
    transition: background-color 0.3s ease;
    position: relative;
}

/* =====================================================================
   ERROR MESSAGES
   ===================================================================== */
.fc-error-text {
    color: var(--fc-error-color, #ed4b9e);
}

/* =====================================================================
   HOVER & INTERACTION
   ===================================================================== */
.fc .fc-daygrid-day:not(.fc-day-past) {
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.fc .fc-daygrid-day:not(.fc-day-past):not(.fc-day-selected):hover {
    background-color: var(--fc-day-hover-bg, #f0f9ff);
}

.ppv2-fc-root .fc-daygrid-day-frame {
    display: flex !important;
    flex-direction: column !important;
}

/* =====================================================================
   DAY HEADER ROW (LUNDI, MARDI, etc.)
   ===================================================================== */
.ppv2-fc-root .fc .fc-col-header {
    background-color: var(--fc-dayheader-bg, #4b5563);
}

.ppv2-fc-root .fc .fc-col-header-cell {
    text-align: left;
    padding: 0 !important;
    border-color: rgba(255, 255, 255, 0.15);
}

.ppv2-fc-root .fc .fc-col-header-cell-cushion {
    color: var(--fc-dayheader-text, #fff) !important;
    font-size: 10px !important;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-decoration: none;
}

/* =====================================================================
   DAY NUMBERS & LINKS
   ===================================================================== */
.ppv2-fc-root .fc .fc-daygrid-day-number {
    text-decoration: none;
    color: var(--fc-header-text, #1e293b);
    font-weight: 500;
    padding: 6px 8px;
    transition: color 0.2s ease;
}

/* =====================================================================
   NAV BUTTONS (prev/next arrows)
   ===================================================================== */
.fc-nav-btn {
    cursor: pointer;
    border-radius: 50%;
    padding: 4px;
    transition:
        background-color 0.15s ease,
        transform 0.1s ease;
}

.fc-nav-btn:hover {
    background-color: rgba(0, 0, 0, 0.08);
    transform: scale(1.1);
}

/* =====================================================================
   GAP FIXES (toolbar → header → body)
   ===================================================================== */
.fc .fc-header-toolbar {
    margin-bottom: 0 !important;
}

.fc table.fc-scrollgrid {
    border-spacing: 0 !important;
}

.fc .fc-scrollgrid-section-header > td {
    padding-bottom: 0 !important;
}

.fc .fc-scrollgrid-section-body > td {
    padding-top: 0 !important;
}
/*
table:not([cellpadding]) th,
table:not([cellpadding]) td {
    padding: 0 !important;
}*/
/* =====================================================================
   RESPONSIVE FORM GRID (dialog date/time pickers)
   ===================================================================== */
.fc-dialog-grid-2col {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
}

@media (max-width: 599px) {
    .fc-dialog-grid-2col {
        grid-template-columns: 1fr;
    }

    .fc-day-checkbox {
        width: 12px;
        height: 12px;
    }

    .fc-day-checkbox-wrapper {
        top: 2px;
        right: 2px;
    }
}

/* =====================================================================
   HEADER SELECT (Mois/Semaine/Jour dropdown)
   ===================================================================== */
.fc-header-select {
    opacity: 1 !important;
}

.fc-header-select-input .v-select {
    --vs-border: 1.5px solid var(--fc-border-color, #d1d5db) !important;
    --vs-border-radius: 8px !important;
    --vs-font-size: 13px;
    --vs-padding: 2px 0;
    --vs-color: var(--fc-header-text, #1e293b) !important;
    --vs-placeholder-color: var(--fc-header-text, #1e293b);
    background: var(--fc-page-bg-color, #fff);
    border-radius: 8px;
}

.fc-header-select-input .vs__selected {
    color: var(--fc-header-text, #1e293b) !important;
    font-size: 13px;
    font-weight: 500;
}

.fc-header-select-input .vs__open-indicator {
    fill: var(--fc-header-text, #1e293b) !important;
}

.fc-header-select-input .vs__dropdown-menu {
    background: var(--fc-page-bg-color, #fff) !important;
    color: var(--fc-header-text, #1e293b) !important;
    border: 1px solid var(--fc-border-color, #d1d5db) !important;
    border-radius: 8px !important;
    min-width: 110px;
}

.fc-header-select-input .vs__dropdown-option {
    font-size: 11px;
}
.fc .fc-daygrid-day-number {
    font-size: 10px;
}

/* =====================================================================
   EVENT TEXT TRUNCATION
   ===================================================================== */
.ppv2-fc-root .fc .fc-daygrid-day-events {
    overflow: hidden;
    min-height: 0 !important;
    margin-top: auto !important;
    margin-bottom: auto !important;
    width: 100%;
}

.ppv2-fc-root .fc .fc-daygrid-event-harness {
    overflow: hidden;
    margin-bottom: 1px;
}

.ppv2-fc-root .fc .fc-daygrid-event {
    overflow: hidden !important;
    white-space: normal !important;
    border-radius: 3px;
    margin: 0 1px 1px 1px;
    padding: 0 !important;
}

.ppv2-fc-root .fc .fc-event-main {
    overflow: hidden !important;
    padding: 0 !important;
    justify-content: flex-start !important;
    align-items: flex-start !important;
    text-align: left !important;
}

.ppv2-fc-root .fc .fc-event-main-frame {
    overflow: hidden !important;
    flex-direction: column !important;
    justify-content: flex-start !important;
    align-items: flex-start !important;
    text-align: left !important;
}

.ppv2-fc-root .fc .fc-event-title-container {
    overflow: hidden !important;
    justify-content: flex-start !important;
    align-items: flex-start !important;
    text-align: left !important;
}

.ppv2-fc-root .fc .fc-event-title {
    overflow: hidden !important;
    text-align: left !important;
}

.fc-event-text {
    font-size: 0.6rem;
    line-height: 1.1;
    padding: 1px 2px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
    min-width: 0;
    text-align: left;
    align-items: flex-start;
}

.fc-event-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 700;
    font-size: 0.6rem;
    line-height: 1.1;
    text-align: left;
    display: block;
    width: 100%;
}

.fc-event-time {
    opacity: 0.75;
    font-size: 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.1;
    text-align: center;
    display: block;
    width: 100%;
}

/* =====================================================================
   EVENT TOOLTIP
   ===================================================================== */
.fc-event-tooltip {
    position: absolute;
    background: var(--fc-tooltip-bg, #1e293b);
    color: var(--fc-tooltip-text, #f8fafc);
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    pointer-events: none;
    z-index: 1000;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    animation: fc-tooltip-in 0.12s ease;
}

@keyframes fc-tooltip-in {
    from {
        opacity: 0;
        transform: translateY(-3px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* =====================================================================
   MAXIMIZE STYLING
   ===================================================================== */
.ppv2-fc-root.is-maximized {
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    box-shadow:
        0 10px 25px -5px rgba(0, 0, 0, 0.1),
        0 8px 10px -6px rgba(0, 0, 0, 0.1);
}

.ppv2-fc-root.is-maximized .fc {
    flex: 1;
    min-height: 0;
    height: 100% !important;
}

/* Ensure header stays visible and sticky if needed, though with flex it's fine */
.ppv2-fc-root.is-maximized > div:first-child {
    flex-shrink: 0;
    margin-bottom: 20px;
    border-radius: 12px;
}
</style>
